/**
 * Global search command palette (Ctrl+K)
 */
import { useEffect, useState, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Loader2 } from 'lucide-react';
import { api, type WebhookEvent } from '@/lib/api';
import { useFocusTrap, announce } from '@/lib/accessibility';

interface SearchResult {
  type: 'event' | 'page';
  id?: string;
  title: string;
  subtitle?: string;
  icon?: React.ReactNode;
  url: string;
}

const PAGE_RESULTS: SearchResult[] = [
  { type: 'page', title: 'Dashboard', subtitle: 'View system overview', url: '/dashboard' },
  { type: 'page', title: 'Events', subtitle: 'View webhook events', url: '/events' },
  { type: 'page', title: 'Deliveries', subtitle: 'View delivery attempts', url: '/deliveries' },
  { type: 'page', title: 'Subscriptions', subtitle: 'Manage subscriptions', url: '/subscriptions' },
  { type: 'page', title: 'API Keys', subtitle: 'Manage API keys', url: '/keys' },
  { type: 'page', title: 'Health', subtitle: 'System health status', url: '/health' },
  { type: 'page', title: 'Costs', subtitle: 'View cost metrics', url: '/costs' },
  { type: 'page', title: 'Aggregates', subtitle: 'View aggregated data', url: '/aggregates' },
];

interface GlobalSearchProps {
  isOpen: boolean;
  onClose: () => void;
}

export function GlobalSearch({ isOpen, onClose }: GlobalSearchProps) {
  const [query, setQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [events, setEvents] = useState<WebhookEvent[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const navigate = useNavigate();
  const searchRef = useRef<HTMLDivElement>(null);

  // Focus trap
  useFocusTrap(isOpen, searchRef);

  // Combined search results
  const results = useMemo(() => {
    const q = query.toLowerCase().trim();
    if (!q) return PAGE_RESULTS;

    const results: SearchResult[] = [];

    // Page results
    PAGE_RESULTS.forEach(page => {
      if (page.title.toLowerCase().includes(q) || page.subtitle?.toLowerCase().includes(q)) {
        results.push(page);
      }
    });

    // Event results
    events.slice(0, 10).forEach(event => {
      results.push({
        type: 'event',
        id: event.id,
        title: `${event.event_type}${event.action ? `.${event.action}` : ''}`,
        subtitle: event.repository || `Event ${event.id.substring(0, 8)}`,
        url: `/events?id=${event.id}`,
      });
    });

    return results;
  }, [query, events]);

  // Search API on query change
  useEffect(() => {
    if (!query.trim() || !isOpen) return;

    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        // Only search events (deliveries don't have global search)
        const eventsRes = await api.events.list({ limit: 10, event_type: query });
        setEvents(eventsRes.events || []);
        
        // Announce results to screen readers
        const totalResults = (eventsRes.events?.length || 0);
        announce(`${totalResults} result${totalResults === 1 ? '' : 's'} found`);
      } catch (error) {
        console.error('Search failed:', error);
        announce('Search failed', 'assertive');
      } finally {
        setSearching(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [query, isOpen]);

  // Handle keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex(i => Math.min(i + 1, results.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex(i => Math.max(i - 1, 0));
      } else if (e.key === 'Enter' && results[selectedIndex]) {
        e.preventDefault();
        navigate(results[selectedIndex].url);
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, results, selectedIndex, navigate, onClose]);

  // Reset state when closed
  useEffect(() => {
    if (!isOpen) {
      setQuery('');
      setEvents([]);
      setSelectedIndex(0);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-[15vh]" role="dialog" aria-modal="true" aria-label="Global search">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Search Modal */}
      <div ref={searchRef} className="relative z-10 w-full max-w-2xl mx-4 bg-white dark:bg-gray-900 border border-border rounded-lg shadow-2xl overflow-hidden">
        {/* Search Input */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-border">
          <Search className="h-5 w-5 text-muted-foreground" aria-hidden="true" />
          <input
            type="text"
            role="combobox"
            aria-controls="search-results"
            aria-expanded={results.length > 0}
            aria-activedescendant={results[selectedIndex] ? `result-${selectedIndex}` : undefined}
            placeholder="Search pages, events, deliveries..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            autoFocus
            className="flex-1 bg-transparent border-none outline-none text-sm placeholder:text-muted-foreground"
          />
          {searching && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
        </div>

        {/* Results */}
        <div id="search-results" role="listbox" className="max-h-[60vh] overflow-y-auto">
          {results.length === 0 && query && (
            <div className="px-4 py-8 text-center text-sm text-muted-foreground">
              No results found for "{query}"
            </div>
          )}
          {results.map((result, idx) => (
            <button
              key={`${result.type}-${result.id || result.title}`}
              id={`result-${idx}`}
              role="option"
              aria-selected={idx === selectedIndex}
              onClick={() => {
                navigate(result.url);
                onClose();
              }}
              className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${
                idx === selectedIndex
                  ? 'bg-primary/10 text-primary'
                  : 'hover:bg-muted/50 text-foreground'
              }`}
            >
              <div className="flex-1">
                <div className="text-sm font-medium">{result.title}</div>
                {result.subtitle && (
                  <div className="text-xs text-muted-foreground">{result.subtitle}</div>
                )}
              </div>
              <div className="text-xs text-muted-foreground capitalize">{result.type}</div>
            </button>
          ))}
        </div>

        {/* Footer */}
        <div className="px-4 py-2 border-t border-border bg-muted/30 flex items-center justify-between text-xs text-muted-foreground">
          <div className="flex gap-4">
            <span><kbd className="px-1.5 py-0.5 bg-background border border-border rounded">↑↓</kbd> Navigate</span>
            <span><kbd className="px-1.5 py-0.5 bg-background border border-border rounded">Enter</kbd> Select</span>
            <span><kbd className="px-1.5 py-0.5 bg-background border border-border rounded">ESC</kbd> Close</span>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Hook to manage global search state
 */
export function useGlobalSearch() {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ctrl+K or Cmd+K opens search
      if (e.key === 'k' && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        setIsOpen(true);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  return {
    isOpen,
    open: () => setIsOpen(true),
    close: () => setIsOpen(false),
  };
}
